import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { logger } from '../utils/logger';

const { 
  FiSearch, FiFilter, FiPlus, FiMoreVertical, FiMail, FiPhone, 
  FiCalendar, FiArrowRight, FiCheckCircle, FiClock, FiTag, 
  FiEdit, FiTrash2, FiExternalLink, FiUser, FiActivity, FiUsers,
  FiGithub, FiTwitter, FiLinkedin, FiGlobe, FiPlusCircle, FiX, FiHash,
  FiFileText, FiPin, FiMessageSquare, FiTrendingUp, FiTarget, FiStar,
  FiInfo, FiShield
} = FiIcons;

const STATUS_COLORS = {
  Active: 'bg-emerald-500/10 text-emerald-400',
  Inactive: 'bg-slate-500/10 text-slate-500',
  Lead: 'bg-indigo-500/10 text-indigo-400',
  Suspended: 'bg-rose-500/10 text-rose-400'
};

const ClientNote = ({ note, onTogglePin, onDelete }) => (
  <motion.div 
    layout
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`p-6 rounded-3xl border ${note.is_pinned ? 'bg-indigo-500/10 border-indigo-500/30 ring-1 ring-indigo-500/20' : 'bg-white/5 border-white/5'} group relative`}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center text-slate-500">
          <SafeIcon icon={FiFileText} className="text-xs" />
        </div>
        <div>
          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
            {new Date(note.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>
      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onTogglePin(note)} className={`p-2 rounded-lg transition-colors ${note.is_pinned ? 'text-amber-400 bg-amber-400/10' : 'text-slate-500 hover:text-white bg-white/5'}`}>
          <SafeIcon icon={FiPin} className="text-xs" />
        </button>
        <button onClick={() => onDelete(note.id)} className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 bg-white/5 transition-colors">
          <SafeIcon icon={FiTrash2} className="text-xs" />
        </button>
      </div>
    </div>
    <p className="text-sm text-slate-300 leading-relaxed font-medium">{note.content}</p>
    {note.is_pinned && <div className="absolute top-4 right-4"><SafeIcon icon={FiStar} className="text-amber-400 text-[10px] fill-amber-400" /></div>}
  </motion.div>
);

export default function CRM() {
  const { addToast } = useToast();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients_2024')
        .select(`
          *, 
          enrollments:enrollments_2024(count),
          achievements:client_achievements_2024(count)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      logger.error('Failed to fetch clients', { error: err.message });
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientNotes = async (clientId) => {
    setNotesLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_notes_2024')
        .select('*')
        .eq('client_id', clientId)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setNotes(data || []);
    } catch (err) {
      logger.error('Failed to fetch client notes', { clientId, error: err.message });
      addToast(err.message, 'error');
    } finally {
      setNotesLoading(false);
    }
  };

  const handleOpenDetail = (client) => {
    setSelectedClient(client);
    setIsDetailOpen(true);
    fetchClientNotes(client.id);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      addToast("Authentication session expired", "error");
      setModalLoading(false);
      return;
    }
    
    const payload = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      status: formData.get('status'),
      bio: formData.get('bio'),
      social_links: {
        twitter: formData.get('twitter'),
        linkedin: formData.get('linkedin'),
        github: formData.get('github'),
        website: formData.get('website')
      },
      tags: formData.get('tags')?.split(',').map(t => t.trim()).filter(Boolean) || [],
      coach_id: user.id // Explicitly set to ensure RLS compliance
    };

    try {
      if (selectedClient && isFormOpen) {
        const { error } = await supabase.from('clients_2024').update(payload).eq('id', selectedClient.id);
        if (error) throw error;
        addToast("Client profile refined");
      } else {
        const { error } = await supabase.from('clients_2024').insert([payload]);
        if (error) throw error;
        addToast("New high-performance client onboarded");
      }
      setIsFormOpen(false);
      fetchClients();
    } catch (err) {
      logger.error('Client Save Failure', { payload, error: err.message });
      addToast(err.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('client_notes_2024')
        .insert([{
          client_id: selectedClient.id,
          content: newNote,
          is_pinned: false,
          coach_id: user.id
        }])
        .select();

      if (error) throw error;
      setNotes([data[0], ...notes]);
      setNewNote('');
      addToast("Strategic note captured");
    } catch (err) {
      logger.error('Note Creation Failure', { clientId: selectedClient?.id, error: err.message });
      addToast(err.message, 'error');
    }
  };

  const handleTogglePinNote = async (note) => {
    try {
      const { error } = await supabase
        .from('client_notes_2024')
        .update({ is_pinned: !note.is_pinned })
        .eq('id', note.id);
      
      if (error) throw error;
      setNotes(notes.map(n => n.id === note.id ? { ...n, is_pinned: !n.is_pinned } : n).sort((a, b) => b.is_pinned - a.is_pinned));
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const { error } = await supabase.from('client_notes_2024').delete().eq('id', noteId);
      if (error) throw error;
      setNotes(notes.filter(n => n.id !== noteId));
      addToast("Note expunged");
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleDeleteClient = async () => {
    try {
      const { error } = await supabase.from('clients_2024').delete().eq('id', selectedClient.id);
      if (error) throw error;
      addToast("Client record archived");
      setIsDeleteOpen(false);
      setIsDetailOpen(false);
      fetchClients();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const filteredClients = clients.filter(c => {
    const matchesFilter = activeFilter === 'all' || c.status?.toLowerCase() === activeFilter;
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         c.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  if (loading) return (
    <div className="h-[60vh] flex flex-col items-center justify-center space-y-6">
      <div className="w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
      <p className="text-slate-500 font-black text-[10px] uppercase tracking-[0.3em] animate-pulse">Syncing SessionHQ Roster...</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-12 pb-32">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 bg-slate-900/40 backdrop-blur-3xl p-10 rounded-[3rem] border border-white/5 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/5 blur-[120px] rounded-full -mr-48 -mt-48 transition-all group-hover:bg-indigo-500/10" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 border border-indigo-500/20">
              <SafeIcon icon={FiUsers} />
            </div>
            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em]">SessionHQ Intelligence</span>
          </div>
          <h1 className="text-5xl font-black text-white tracking-tighter uppercase leading-none">Client Hub</h1>
          <p className="text-slate-400 mt-4 text-lg font-medium max-w-xl leading-relaxed">Systematic performance tracking and relationship management for elite rosters.</p>
        </div>
        
        <button 
          onClick={() => { setSelectedClient(null); setIsFormOpen(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-5 rounded-3xl font-black text-[11px] uppercase tracking-widest shadow-2xl shadow-indigo-600/30 transition-all flex items-center gap-3 transform hover:scale-105 active:scale-95 relative z-10"
        >
          <SafeIcon icon={FiPlus} className="text-lg" /> Onboard Student
        </button>
      </header>

      {/* Roster Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Active Roster', val: clients.filter(c => c.status === 'Active').length, icon: FiUsers, color: 'text-indigo-400' },
          { label: 'Total Enrollment', val: clients.length, icon: FiTrendingUp, color: 'text-emerald-400' },
          { label: 'Avg Progress', val: '78%', icon: FiActivity, color: 'text-amber-400' },
          { label: 'Lead Pipeline', val: clients.filter(c => c.status === 'Lead').length, icon: FiTarget, color: 'text-purple-400' }
        ].map((stat, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/10 p-8 rounded-[2.5rem] flex items-center justify-between backdrop-blur-xl group hover:border-white/20 transition-all">
             <div>
               <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">{stat.label}</p>
               <h3 className="text-3xl font-black text-white tracking-tight">{stat.val}</h3>
             </div>
             <div className={`w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center ${stat.color} group-hover:scale-110 transition-transform border border-white/5`}>
               <SafeIcon icon={stat.icon} className="text-xl" />
             </div>
          </div>
        ))}
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col md:flex-row gap-6 justify-between items-center bg-white/[0.02] border border-white/10 p-6 rounded-[2.5rem] backdrop-blur-2xl">
        <div className="flex gap-3 p-1.5 bg-black/40 rounded-[1.5rem] border border-white/5">
          {['all', 'active', 'inactive', 'lead'].map(f => (
            <button 
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`px-8 py-3 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all ${activeFilter === f ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-[400px]">
          <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500" />
          <input 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or tag..." 
            className="w-full bg-black/40 border border-white/5 rounded-[1.5rem] pl-16 pr-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all placeholder:text-slate-600"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence mode='popLayout'>
          {filteredClients.map((client, idx) => (
            <motion.div 
              key={client.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => handleOpenDetail(client)}
              className="bg-white/[0.02] border border-white/10 p-10 rounded-[3.5rem] hover:bg-white/[0.05] transition-all cursor-pointer group relative overflow-hidden backdrop-blur-3xl"
            >
               <div className="absolute top-0 right-0 p-8">
                  <div className={`px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-[0.2em] border border-white/5 ${STATUS_COLORS[client.status] || STATUS_COLORS.Inactive}`}>
                    {client.status}
                  </div>
               </div>

               <div className="flex flex-col items-center text-center mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-indigo-500/20 to-purple-600/20 rounded-[2.5rem] flex items-center justify-center text-3xl font-black text-indigo-400 border border-indigo-500/30 group-hover:scale-110 transition-transform mb-6 shadow-2xl relative">
                    {client.avatar_url ? (
                      <img src={client.avatar_url} alt={client.name} className="w-full h-full object-cover rounded-[2.5rem]" />
                    ) : (
                      client.name[0]
                    )}
                    <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-slate-900 border border-white/10 rounded-2xl flex items-center justify-center text-slate-500">
                      <SafeIcon icon={FiUser} className="text-xs" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-black text-white tracking-tighter uppercase group-hover:text-indigo-400 transition-colors">{client.name}</h3>
                  <p className="text-xs text-slate-500 font-bold tracking-tight mt-1">{client.email}</p>
               </div>

               {client.tags && client.tags.length > 0 && (
                 <div className="flex flex-wrap justify-center gap-2 mb-8">
                   {client.tags.slice(0, 3).map(tag => (
                     <span key={tag} className="px-3 py-1 bg-white/5 rounded-lg text-[8px] font-black text-slate-400 uppercase tracking-widest border border-white/5">
                       #{tag}
                     </span>
                   ))}
                   {client.tags.length > 3 && <span className="text-[8px] text-slate-600 font-black">+{client.tags.length - 3}</span>}
                 </div>
               )}
               
               <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-8">
                  <div className="flex flex-col items-center py-4 bg-white/5 rounded-3xl border border-white/5">
                    <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">Courses</span>
                    <span className="text-lg font-black text-white">{client.enrollments?.[0]?.count || 0}</span>
                  </div>
                  <div className="flex flex-col items-center py-4 bg-white/5 rounded-3xl border border-white/5">
                    <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">Milestones</span>
                    <span className="text-lg font-black text-white">{client.achievements?.[0]?.count || 0}</span>
                  </div>
               </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {isDetailOpen && selectedClient && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setIsDetailOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-xl z-[60]" 
            />
            <motion.div 
              initial={{ x: '100%' }} 
              animate={{ x: 0 }} 
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-[#0a0a0a] border-l border-white/10 z-[70] shadow-2xl p-0 flex flex-col"
            >
              <div className="p-10 flex justify-between items-center bg-white/[0.02] border-b border-white/5">
                 <div className="flex items-center gap-6">
                    <div className="w-16 h-16 bg-indigo-500/10 rounded-2xl flex items-center justify-center text-2xl font-black text-indigo-400 border border-indigo-500/20">
                      {selectedClient.name[0]}
                    </div>
                    <div>
                      <h2 className="text-3xl font-black text-white tracking-tighter uppercase">{selectedClient.name}</h2>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-[8px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-white/5 ${STATUS_COLORS[selectedClient.status] || STATUS_COLORS.Inactive}`}>
                          {selectedClient.status}
                        </span>
                        <span className="text-[9px] text-slate-500 font-bold font-mono">ID: {selectedClient.id.slice(0, 8)}</span>
                      </div>
                    </div>
                 </div>
                 <div className="flex gap-3">
                   <button 
                     onClick={() => { setIsFormOpen(true); }}
                     className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/10"
                   >
                     <SafeIcon icon={FiEdit} />
                   </button>
                   <button 
                     onClick={() => setIsDetailOpen(false)}
                     className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/10"
                   >
                     <SafeIcon icon={FiX} />
                   </button>
                 </div>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar p-10 space-y-12">
                <section className="space-y-6">
                  <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.3em] flex items-center gap-3">
                    <SafeIcon icon={FiInfo} /> Intelligence & Contact
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-6 bg-white/[0.02] border border-white/5 rounded-[2rem] space-y-2">
                       <label className="text-[8px] font-black text-slate-600 uppercase tracking-widest block">Email Terminal</label>
                       <p className="text-sm text-white font-bold truncate">{selectedClient.email}</p>
                    </div>
                    <div className="p-6 bg-white/[0.02] border border-white/5 rounded-[2rem] space-y-2">
                       <label className="text-[8px] font-black text-slate-600 uppercase tracking-widest block">Direct Communications</label>
                       <p className="text-sm text-white font-bold">{selectedClient.phone || 'NO SECURE LINE'}</p>
                    </div>
                  </div>
                  
                  <div className="p-8 bg-white/[0.02] border border-white/5 rounded-[2.5rem]">
                    <label className="text-[8px] font-black text-slate-600 uppercase tracking-widest block mb-4">Strategic Bio</label>
                    <p className="text-sm text-slate-400 leading-relaxed font-medium italic">
                      "{selectedClient.bio || 'No strategic overview provided for this client profile.'}"
                    </p>
                  </div>

                  <div className="flex gap-4">
                    {[
                      { icon: FiTwitter, key: 'twitter' },
                      { icon: FiLinkedin, key: 'linkedin' },
                      { icon: FiGithub, key: 'github' },
                      { icon: FiGlobe, key: 'website' }
                    ].map(social => selectedClient.social_links?.[social.key] && (
                      <a key={social.key} href={selectedClient.social_links[social.key]} target="_blank" rel="noreferrer" className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-slate-500 hover:text-indigo-400 hover:bg-indigo-500/10 transition-all border border-white/5">
                        <SafeIcon icon={social.icon} />
                      </a>
                    ))}
                  </div>
                </section>

                <section className="space-y-6">
                  <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.3em] flex items-center gap-3">
                    <SafeIcon icon={FiHash} /> Segmentation
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    {selectedClient.tags?.map(tag => (
                      <span key={tag} className="px-6 py-3 bg-indigo-500/10 rounded-2xl text-[10px] font-black text-indigo-400 uppercase tracking-widest border border-indigo-500/20 flex items-center gap-2">
                        <SafeIcon icon={FiTag} className="text-[10px]" /> {tag}
                      </span>
                    )) || <p className="text-xs text-slate-600 font-bold uppercase tracking-widest">No segments assigned</p>}
                  </div>
                </section>

                <section className="space-y-8">
                  <div className="flex justify-between items-center">
                    <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.3em] flex items-center gap-3">
                      <SafeIcon icon={FiMessageSquare} /> Strategic Intelligence
                    </h4>
                    {notesLoading && <div className="w-4 h-4 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />}
                  </div>

                  <form onSubmit={handleAddNote} className="relative">
                    <input 
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Capture new intelligence node..." 
                      className="w-full bg-white/5 border border-white/10 rounded-[2rem] pl-8 pr-16 py-6 text-sm text-white font-medium outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
                    />
                    <button type="submit" className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20">
                      <SafeIcon icon={FiPlus} />
                    </button>
                  </form>

                  <div className="space-y-4">
                    {notes.map(note => (
                      <ClientNote 
                        key={note.id} 
                        note={note} 
                        onTogglePin={handleTogglePinNote} 
                        onDelete={handleDeleteNote} 
                      />
                    ))}
                  </div>
                </section>
                
                <div className="pt-10 border-t border-white/5">
                   <button 
                     onClick={() => setIsDeleteOpen(true)}
                     className="flex items-center gap-3 text-rose-500 font-black text-[10px] uppercase tracking-widest hover:bg-rose-500/10 px-6 py-3 rounded-xl transition-all"
                   >
                     <SafeIcon icon={FiTrash2} /> Archival Protocol
                   </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <FormModal 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
        title={selectedClient ? "Refine Student Identity" : "Elite Onboarding Initiative"} 
        onSubmit={handleSaveClient}
        loading={modalLoading}
        width="max-w-4xl"
      >
        <div className="space-y-12 max-h-[75vh] overflow-y-auto pr-6 custom-scrollbar pb-10">
          <section className="space-y-8">
            <div className="flex items-center gap-4 border-b border-white/5 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                <SafeIcon icon={FiUser} />
              </div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Core Identity</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Full Professional Name</label>
                <input name="name" defaultValue={selectedClient?.name} required className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" placeholder="e.g. Alexander Sterling" />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Status Tier</label>
                <select name="status" defaultValue={selectedClient?.status || 'Active'} className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 appearance-none">
                  <option value="Active">ACTIVE</option>
                  <option value="Lead">LEAD</option>
                  <option value="Inactive">INACTIVE</option>
                  <option value="Suspended">SUSPENDED</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Email Endpoint</label>
                <input name="email" type="email" defaultValue={selectedClient?.email} required className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" placeholder="alex@sterling.com" />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Direct Secure Line</label>
                <input name="phone" defaultValue={selectedClient?.phone} className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" placeholder="+1 (555) 000-0000" />
              </div>
            </div>
          </section>

          <section className="space-y-8">
            <div className="flex items-center gap-4 border-b border-white/5 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                <SafeIcon icon={FiGlobe} />
              </div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Intelligence & Socials</h3>
            </div>
            
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Strategic Bio</label>
              <textarea name="bio" defaultValue={selectedClient?.bio} rows="4" className="w-full bg-white/5 border border-white/10 rounded-[2rem] px-8 py-6 text-sm text-white font-medium outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all resize-none" placeholder="Contextual background on the client's goals and current performance..." />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Segmentation Tags (Comma Separated)</label>
                <input name="tags" defaultValue={selectedClient?.tags?.join(', ')} className="w-full bg-white/5 border border-white/10 rounded-2xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" placeholder="VIP, High-Ticket, Q3-Onboard" />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Digital Presence (Social Links)</label>
                <div className="grid grid-cols-2 gap-4">
                  <input name="twitter" defaultValue={selectedClient?.social_links?.twitter} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white outline-none" placeholder="Twitter URL" />
                  <input name="linkedin" defaultValue={selectedClient?.social_links?.linkedin} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white outline-none" placeholder="LinkedIn URL" />
                  <input name="github" defaultValue={selectedClient?.social_links?.github} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white outline-none" placeholder="GitHub URL" />
                  <input name="website" defaultValue={selectedClient?.social_links?.website} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white outline-none" placeholder="Website URL" />
                </div>
              </div>
            </div>
          </section>
        </div>
      </FormModal>

      <ConfirmModal 
        isOpen={isDeleteOpen} 
        onClose={() => setIsDeleteOpen(false)} 
        onConfirm={handleDeleteClient}
        title="Protocol: Archive Record" 
        message="Permanently expunge this client profile from your active roster? This action is irreversible and will purge all associated strategic notes."
      />

      <motion.div 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 px-10 py-5 bg-slate-900/80 backdrop-blur-3xl border border-white/10 rounded-full shadow-2xl flex items-center gap-8"
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Roster Synchronized</span>
        </div>
        <div className="h-5 w-px bg-white/10" />
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiShield} className="text-indigo-400" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">TLS 1.3 Encryption Active</span>
        </div>
      </motion.div>
    </div>
  );
}